import { ILayoutBaseTestGen } from '../layoutBaseTest';
import {
  PdfGen,
  PdfFont,
  PdfRect,
  PdfBrush,
  ETextAlign,
  PdfUnit,
  PdfSize,
  PdfColor,
  PdfPen,
} from '../../pdf/pdf';
import '../../pdf/draw/string';
import '../../pdf/draw/primitives';
import '../../pdf/draw/shapes';
import { moduleRegistry } from '../../modules';
import { chartModule } from '../../modules/chart';
import { getAllPaletteNames, getPalette } from '../../pdf/palette';
import { OptsBaseTest } from '../optsBaseTest';

const MM = PdfUnit.FromMillimeters;
const MARGIN = MM(12);
const PAGE_WIDTH = PdfSize.A4.width;
const PAGE_HEIGHT = PdfSize.A4.height;

export class TestPalette implements ILayoutBaseTestGen {
  async generate(pdf: PdfGen, opts: OptsBaseTest): Promise<void> {
    await pdf.addStandardFonts();

    moduleRegistry.register(chartModule);

    const font = new PdfFont(PdfFont.FONT_NAME, 14);
    const smallFont = new PdfFont(PdfFont.FONT_NAME, 10);
    const tinyFont = new PdfFont(PdfFont.FONT_NAME, 8);

    const chart = moduleRegistry.get('chart');
    if (!chart) {
      throw new Error('Chart module not found');
    }

    const titleRect = new PdfRect(MARGIN, MM(5), PdfSize.A4.width - 2 * MARGIN, MM(8));

    const paletteNames = getAllPaletteNames();

    const allPalettes = paletteNames.map((name) => getPalette(name));
    const maxColors = Math.max(...allPalettes.map((p) => p.colors.length));

    const availableWidth = PAGE_WIDTH - 2 * MARGIN;
    const swatchWidth = (availableWidth - 5) / maxColors;
    const swatchHeight = MM(20);

    const testData = `| Item | Value |
| ---- | ----- |
| A    |    80 |
| B    |    60 |
| C    |    45 |
| D    |    30 |
| E    |    20 |`;

    const pieData = `| Label | Value |
| ----- | ----- |
| X     |    35 |
| Y     |    25 |
| Z     |    20 |
| W     |    15 |
| V     |     5 |`;

    const lineData = `| Month | Value |
| ----- | ----- |
| Jan   |    20 |
| Feb   |    45 |
| Mar   |    30 |
| Apr   |    60 |
| May   |    50 |`;

    const grayBrush = new PdfBrush(PdfColor.fromRgb(100, 100, 100));

    const drawSwatch = (
      page: ReturnType<PdfGen['addPage']>,
      x: number,
      y: number,
      width: number,
      height: number,
      colorHex: string,
      textColorHex: string,
      outlineColorHex: string | null
    ) => {
      const color = PdfColor.fromHex(colorHex);
      const textColor = PdfColor.fromHex(textColorHex);

      const outline = outlineColorHex ? new PdfPen(PdfColor.fromHex(outlineColorHex), 0.3) : null;

      page.drawRectangle(outline, new PdfBrush(color), new PdfRect(x, y, width, height));

      const hexLabel = colorHex;
      page.drawString(
        hexLabel,
        tinyFont,
        new PdfBrush(textColor),
        null,
        new PdfRect(x, y + height / 2 - 2, width, 4),
        ETextAlign.Center
      );
    };

    const lightPalettes = ['soft', 'pastelStrong', 'warmLight', 'coolLight'];
    const darkPalettes = ['deep', 'professional', 'academic', 'grayscaleDark'];

    // Page 1: Color Swatches - Light Palettes (black text)
    const page1 = pdf.addPage();
    page1.drawString(
      'Palette Tests - Light Palettes (Black Text)',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );
    page1.drawString(
      'These palettes use light colors with black text for maximum readability.',
      smallFont,
      grayBrush,
      null,
      new PdfRect(MARGIN, MM(10), PdfSize.A4.width - 2 * MARGIN, MM(5)),
      ETextAlign.CenterLeft
    );

    let yPos = MM(20);

    for (const paletteName of lightPalettes) {
      const palette = getPalette(paletteName);
      page1.drawString(
        `${palette.name} palette:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos, MM(100), MM(5)),
        ETextAlign.CenterLeft
      );
      yPos += MM(6);

      const rowHeight = swatchHeight + MM(2);
      for (let i = 0; i < palette.colors.length; i++) {
        const x = MARGIN + i * swatchWidth;
        drawSwatch(
          page1,
          x,
          yPos,
          swatchWidth,
          swatchHeight,
          palette.colors[i],
          palette.textColor,
          palette.outlineColor
        );
      }
      yPos += rowHeight;
    }

    // Page 2: Color Swatches - Dark Palettes (white text)
    const page2 = pdf.addPage();
    page2.drawString(
      'Palette Tests - Dark Palettes (White Text)',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );
    page2.drawString(
      'These palettes use dark colors with white text for contrast.',
      smallFont,
      grayBrush,
      null,
      new PdfRect(MARGIN, MM(10), PdfSize.A4.width - 2 * MARGIN, MM(5)),
      ETextAlign.CenterLeft
    );

    yPos = MM(20);

    for (const paletteName of darkPalettes) {
      const palette = getPalette(paletteName);
      page2.drawString(
        `${palette.name} palette:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos, MM(100), MM(5)),
        ETextAlign.CenterLeft
      );
      yPos += MM(6);

      const rowHeight = swatchHeight + MM(2);
      for (let i = 0; i < palette.colors.length; i++) {
        const x = MARGIN + i * swatchWidth;
        drawSwatch(
          page2,
          x,
          yPos,
          swatchWidth,
          swatchHeight,
          palette.colors[i],
          palette.textColor,
          palette.outlineColor
        );
      }
      yPos += rowHeight;
    }

    // Page 3: Column Charts with Light Palettes
    const page3 = pdf.addPage();
    page3.drawString(
      'Palette Tests - Column Charts (Light Palettes)',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    yPos = MM(20);
    const colChartHeight = MM(55);
    const colChartSpacing = MM(65);
    for (const paletteName of lightPalettes) {
      const chartRect = new PdfRect(MARGIN, yPos, MM(120), colChartHeight);
      const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
      page3.drawRectangle(outline, null, chartRect);
      page3.drawString(
        `${paletteName}:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos - MM(5), MM(120), MM(5)),
        ETextAlign.CenterLeft
      );
      chart.draw(
        chart.parse(testData, { type: 'column', palette: paletteName }),
        chartRect,
        ETextAlign.CenterLeft,
        { font, page: page3 }
      );
      yPos += colChartSpacing;
    }

    // Page 4: Column Charts with Dark Palettes
    const page4 = pdf.addPage();
    page4.drawString(
      'Palette Tests - Column Charts (Dark Palettes)',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    yPos = MM(20);
    for (const paletteName of darkPalettes) {
      const chartRect = new PdfRect(MARGIN, yPos, MM(120), colChartHeight);
      const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
      page4.drawRectangle(outline, null, chartRect);
      page4.drawString(
        `${paletteName}:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos - MM(5), MM(120), MM(5)),
        ETextAlign.CenterLeft
      );
      chart.draw(
        chart.parse(testData, { type: 'column', palette: paletteName }),
        chartRect,
        ETextAlign.CenterLeft,
        { font, page: page4 }
      );
      yPos += colChartSpacing;
    }

    // Page 5: Pie Charts with Light Palettes
    const page5 = pdf.addPage();
    page5.drawString(
      'Palette Tests - Pie Charts (Light Palettes)',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    yPos = MM(20);
    const pieChartHeight = MM(90);
    const pieChartSpacing = MM(110);
    for (const paletteName of lightPalettes) {
      const chartRect = new PdfRect(MARGIN, yPos, MM(120), pieChartHeight);
      const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
      page5.drawRectangle(outline, null, chartRect);
      page5.drawString(
        `${paletteName}:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos - MM(5), MM(120), MM(5)),
        ETextAlign.CenterLeft
      );
      chart.draw(
        chart.parse(pieData, { type: 'pie', palette: paletteName }),
        chartRect,
        ETextAlign.CenterLeft,
        { font, page: page5 }
      );
      yPos += pieChartSpacing;
    }

    // Page 6: Pie Charts with Dark Palettes
    const page6 = pdf.addPage();
    page6.drawString(
      'Palette Tests - Pie Charts (Dark Palettes)',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    yPos = MM(20);
    for (const paletteName of darkPalettes) {
      const chartRect = new PdfRect(MARGIN, yPos, MM(120), pieChartHeight);
      const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
      page6.drawRectangle(outline, null, chartRect);
      page6.drawString(
        `${paletteName}:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos - MM(5), MM(120), MM(5)),
        ETextAlign.CenterLeft
      );
      chart.draw(
        chart.parse(pieData, { type: 'pie', palette: paletteName }),
        chartRect,
        ETextAlign.CenterLeft,
        { font, page: page6 }
      );
      yPos += pieChartSpacing;
    }

    // Page 7: Line Charts with All Palettes
    const page7 = pdf.addPage();
    page7.drawString(
      'Palette Tests - Line Charts',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    yPos = MM(20);
    const lineChartHeight = MM(55);
    const lineChartSpacing = MM(65);
    for (const paletteName of paletteNames.slice(0, 4)) {
      const chartRect = new PdfRect(MARGIN, yPos, MM(120), lineChartHeight);
      const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
      page7.drawRectangle(outline, null, chartRect);
      page7.drawString(
        `${paletteName}:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos - MM(5), MM(120), MM(5)),
        ETextAlign.CenterLeft
      );
      chart.draw(
        chart.parse(lineData, { type: 'line', palette: paletteName }),
        chartRect,
        ETextAlign.CenterLeft,
        { font, page: page7 }
      );
      yPos += lineChartSpacing;
    }

    // Page 8: Bar Charts with All Palettes
    const page8 = pdf.addPage();
    page8.drawString(
      'Palette Tests - Bar Charts',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    yPos = MM(20);
    const barChartHeight = MM(55);
    const barChartSpacing = MM(65);
    for (const paletteName of paletteNames.slice(0, 4)) {
      const chartRect = new PdfRect(MARGIN, yPos, MM(120), barChartHeight);
      const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
      page8.drawRectangle(outline, null, chartRect);
      page8.drawString(
        `${paletteName}:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos - MM(5), MM(120), MM(5)),
        ETextAlign.CenterLeft
      );
      chart.draw(
        chart.parse(testData, { type: 'bar', palette: paletteName }),
        chartRect,
        ETextAlign.CenterLeft,
        { font, page: page8 }
      );
      yPos += barChartSpacing;
    }

    // Page 9: Area Charts with All Palettes
    const page9 = pdf.addPage();
    page9.drawString(
      'Palette Tests - Area Charts',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    yPos = MM(20);
    const areaChartHeight = MM(55);
    const areaChartSpacing = MM(65);
    for (const paletteName of paletteNames.slice(0, 4)) {
      const chartRect = new PdfRect(MARGIN, yPos, MM(120), areaChartHeight);
      const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
      page9.drawRectangle(outline, null, chartRect);
      page9.drawString(
        `${paletteName}:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos - MM(5), MM(120), MM(5)),
        ETextAlign.CenterLeft
      );
      chart.draw(
        chart.parse(lineData, { type: 'area', palette: paletteName }),
        chartRect,
        ETextAlign.CenterLeft,
        { font, page: page9 }
      );
      yPos += areaChartSpacing;
    }

    // Page 10: All Chart Types with One Palette (professional)
    const page10 = pdf.addPage();
    page10.drawString(
      'Palette Tests - All Chart Types (professional palette)',
      font,
      PdfBrush.Black,
      null,
      titleRect,
      ETextAlign.CenterLeft
    );

    const proPalette = 'professional';
    const chartTypes = [
      { type: 'column', data: testData },
      { type: 'bar', data: testData },
      { type: 'line', data: lineData },
      { type: 'area', data: lineData },
      { type: 'pie', data: pieData },
    ];

    yPos = MM(20);
    const mixedChartHeight = MM(45);
    const mixedChartSpacing = MM(52);
    for (const ct of chartTypes) {
      const chartRect = new PdfRect(MARGIN, yPos, MM(150), mixedChartHeight);
      const outline = new PdfPen(PdfColor.fromRgb(200, 200, 200), 0.5);
      page10.drawRectangle(outline, null, chartRect);
      page10.drawString(
        `${ct.type} chart:`,
        smallFont,
        PdfBrush.Black,
        null,
        new PdfRect(MARGIN, yPos - MM(5), MM(150), MM(5)),
        ETextAlign.CenterLeft
      );
      chart.draw(
        chart.parse(ct.data, { type: ct.type, palette: proPalette }),
        chartRect,
        ETextAlign.CenterLeft,
        { font, page: page10 }
      );
      yPos += mixedChartSpacing;
    }
  }
}
